import { localizedName } from "../utils/i18n.ts";
import type { Card } from "../entities/types.ts";
import { getPersistenceTag } from "./cardPersistence.ts";
import { createMascot } from "./OperatorMascot.ts";
import { swapAdvice } from "./commanderQuips.ts";
import { commsLine } from "./commsI18n.ts";
import { rarityMaterial, RARITY } from "../core/palette.ts";
import { COLORS } from "../core/constants.ts";
import { SkillCard } from "./SkillCard.ts";
import { isReducedMotion } from "../engine/MotionSettings.ts";
import { announce, makeKeyboardActivatable } from "./a11y.ts";
import type { EventBus } from "../core/EventBus.ts";
import { UI_TYPE, UI_FONT_STACK, UI_EASE, UI_DURATION, roleFontSize, withUiScale } from "./uiTokens.ts";

export type SwapCallback = (slotIndex: number) => void;
export type CloseCallback = () => void;
type QuipPicker = (card: Card, rng?: () => number) => string;

const RARITY_RANK: Record<string, number> = {
  common: 0,
  rare: 1,
  epic: 2,
  legendary: 3,
};
const rank = (r: string): number => RARITY_RANK[r] ?? 0;

export class SkillOverview {
  private container: HTMLDivElement;
  private visible_ = false;
  private onClose: CloseCallback | null = null;
  private swapMode = false;
  private pendingCard: Card | null = null;
  /** BL-118 — the deck slot armed for the in-place CONFIRM SWAP (null = none). */
  private selectedSlot: number | null = null;
  private onSwap: SwapCallback | null = null;
  private onSwapCancel: CloseCallback | null = null;
  private maxSlots_ = 5;
  private quipRng: () => number;
  private cards: SkillCard[] = [];
  /** The last VERA line, for the no-immediate-repeat advisor dedup. */
  private lastAdviceLine = "";
  private confirmTimer: ReturnType<typeof setTimeout> | null = null;
  private bus: EventBus | null;

  constructor(
    _allCards: Card[],
    options?: { quipRng?: () => number; quipPicker?: QuipPicker },
    bus?: EventBus,
  ) {
    this.bus = bus ?? null;
    this.container = document.createElement("div");
    this.container.id = "skill-overview";
    // Mount inside the fixed-stage container so this full-screen overlay scales
    // with the rest of the game (S183/BL-201: position:absolute fills the stage
    // frame exactly). Falls back to <body> in headless/test DOM with no stage.
    (document.getElementById("game-container") ?? document.body).appendChild(
      this.container,
    );
    this.quipRng = options?.quipRng ?? Math.random;
    // `quipPicker` (the legacy rarity-only narrator) is accepted for API
    // compatibility but superseded by the contextual `swapAdvice` in swap mode.
    this.injectStyles();
  }

  get visible(): boolean {
    return this.visible_;
  }

  set maxSlots(val: number) {
    this.maxSlots_ = val;
  }

  show(
    activeCards: Card[] = [],
    currentWave = 0,
    onClose: CloseCallback | null = null,
  ): void {
    this.onClose = onClose;
    this.swapMode = false;
    this.pendingCard = null;
    // PT-37: opening this full-screen overlay dims the loop (interlude). Emit
    // only on the not-visible → visible edge so a re-render doesn't re-trigger.
    if (!this.visible_) this.bus?.emit("skill.swap.shown");
    this.visible_ = true;
    this.renderNormal(activeCards, currentWave);
    this.container.classList.add("visible");
    this.container.classList.remove("swap-mode");
  }

  showSwap(
    pendingCard: Card,
    activeCards: Card[],
    currentWave: number,
    onSwap: SwapCallback,
    onCancel: CloseCallback,
  ): void {
    this.swapMode = true;
    this.pendingCard = pendingCard;
    this.selectedSlot = null;
    this.onSwap = onSwap;
    this.onSwapCancel = onCancel;
    if (!this.visible_) this.bus?.emit("skill.swap.shown");
    this.visible_ = true;
    void currentWave;
    this.renderSwap(activeCards);
    this.container.classList.add("visible", "swap-mode");
  }

  hide(): void {
    const wasSwap = this.swapMode;
    // PT-37: lift the interlude on the visible → hidden edge (balances show).
    if (this.visible_) this.bus?.emit("skill.swap.hidden");
    this.visible_ = false;
    this.swapMode = false;
    this.pendingCard = null;
    this.selectedSlot = null;
    if (this.confirmTimer) {
      clearTimeout(this.confirmTimer);
      this.confirmTimer = null;
    }
    this.container.classList.remove("visible", "swap-mode", "confirming");
    this.disposeCards();
    if (!wasSwap && this.onClose) this.onClose();
  }

  private disposeCards(): void {
    for (const c of this.cards) c.destroy();
    this.cards = [];
  }

  private resolvedName(card: Card): string {
    return localizedName("card", card.id, card.name);
  }

  private resolvedDesc(card: Card): string {
    return localizedName("card", `${card.id}.desc`, card.description);
  }

  // ─── Normal mode — the [S] binder (loadout summary + the Trainer-card gallery) ─

  private renderNormal(activeCards: Card[], currentWave: number): void {
    this.disposeCards();
    const loadoutTitle = `ACTIVE SKILLS [${activeCards.length}/${this.maxSlots_}]`;

    let html = `<div class="so-header">
      <div class="so-title">// AI SKILL DATABASE</div>
      <button class="so-close-btn" id="so-close">✕ CLOSE</button>
    </div>`;

    html += `<div class="so-loadout">
      <div class="so-loadout-title">${loadoutTitle}</div>
      <div class="so-loadout-slots">`;
    for (let i = 0; i < this.maxSlots_; i++) {
      const card = activeCards[i];
      if (card) {
        const color = rarityMaterial(card.rarity).mid;
        const name = this.resolvedName(card);
        const persistenceTag = getPersistenceTag(card);
        let durationInfo = "";
        if (card.duration && card._selectedAtWave != null) {
          const remaining = card._selectedAtWave + card.duration - currentWave;
          durationInfo = `<span class="so-slot-duration">${remaining}W</span>`;
        }
        html += `<div class="so-slot so-slot--filled so-rarity-${card.rarity}" style="--so-color:${color}" data-slot="${i}">
            <span class="so-slot-index">[${i + 1}]</span>
            <span class="so-slot-rarity">${card.rarity.charAt(0).toUpperCase()}</span>
            <span class="so-slot-name">${name}</span>
            <span class="so-slot-persistence so-slot-persistence--${persistenceTag.kind}">${persistenceTag.label}</span>
            ${durationInfo}
          </div>`;
      } else {
        html += `<div class="so-slot so-slot--empty">
            <span class="so-slot-index">[${i + 1}]</span>
            <span class="so-slot-name">─ ─ ─ EMPTY ─ ─ ─</span>
          </div>`;
      }
    }
    html += `</div></div>`;

    html +=
      activeCards.length > 0
        ? `<div class="so-section-title">LOADED SKILLS</div><div class="so-grid"></div>`
        : `<div class="so-section-title">NO SKILLS LOADED</div><div class="so-grid"></div>`;

    this.container.innerHTML = html;

    if (activeCards.length > 0) {
      const grid = this.container.querySelector(".so-grid");
      if (grid) {
        activeCards.forEach((card, idx) => {
          const sc = new SkillCard(card, {
            variant: "overview",
            name: this.resolvedName(card),
            description: this.resolvedDesc(card),
            extraClasses: ["so-binder-card"],
          });
          grid.appendChild(sc.el);
          this.cards.push(sc);
          sc.playReveal(idx * 60);
        });
      }
    }

    this.container.querySelector("#so-close")?.addEventListener("click", () => {
      this.hide();
    });
  }

  // ─── Swap mode — the FROZEN side-by-side decision (BL-60 ⑦, UX-first) ─────────

  private renderSwap(activeCards: Card[]): void {
    this.disposeCards();
    const contender = this.pendingCard;
    if (!contender) return;

    const held = activeCards.filter(Boolean);
    const count = held.length;
    const cRank = rank(contender.rarity);
    // Weakest held slot — the single lowest-rarity card (objective, not advice).
    let lowestSlot = -1;
    let lowestRank = Infinity;
    activeCards.forEach((c, i) => {
      if (!c) return;
      const r = rank(c.rarity);
      if (r < lowestRank) {
        lowestRank = r;
        lowestSlot = i;
      }
    });

    // VERA's contextual tip + matching pose (no immediate repeat).
    const adv = swapAdvice(contender, held, {
      rng: this.quipRng,
      avoid: this.lastAdviceLine,
    });
    this.lastAdviceLine = adv.line;

    // The composition (owner ULTRA redesign): the haloed contender centered on the
    // LEFT (the challenger), the `VS →`, and YOUR DECK as one even, harmonious row
    // of 5 on the RIGHT — contender card + deck row share a vertical centre axis.
    // VERA, the advisor voice, sits CENTRED in the footer directly above the two
    // choices. BL-118 — the decision collapses to exactly TWO clear choices,
    // CONFIRM SWAP + KEEP DECK, one press each: no header ✕ keep-duplicate, no
    // floating second confirm layer, no ambiguous BACK. Picking a deck card ARMS
    // CONFIRM SWAP in place (the same surface), so the commit never moves.
    this.container.innerHTML = `
      <div class="so-header so-header--swap">
        <div class="so-title">LOADOUT FULL · ${count}/${this.maxSlots_} — SWAP IN?</div>
      </div>
      <div class="so-swap">
        <div class="so-swap-tag">▶ INCOMING</div>
        <div class="so-swap-deck-title">YOUR DECK [${count}/${this.maxSlots_}] — pick one to replace</div>
        <div class="so-contender-host"></div>
        <div class="so-swap-divider">VS&nbsp;→</div>
        <div class="so-swap-deck"></div>
      </div>
      <div class="so-swap-foot">
        <div class="so-swap-action" id="so-swap-action">Pick a skill to replace.</div>
        <div class="so-swap-stakes">⚠ Swapping UNLOADS the replaced skill — its effect ends for this run.</div>
        <div class="so-swap-vera" id="so-commander">
          <div class="so-swap-vera-label">VERA · ADVISOR</div>
        </div>
        <div class="so-swap-choices">
          <button class="so-confirm-go" id="so-confirm-go" disabled aria-disabled="true">CONFIRM SWAP</button>
          <button class="so-keep-btn" id="so-keep">KEEP DECK</button>
        </div>
      </div>`;

    // The contender — the same full draft Trainer card, flagged the challenger.
    const chost = this.container.querySelector(".so-contender-host");
    if (chost) {
      const cc = new SkillCard(contender, {
        variant: "draft",
        name: this.resolvedName(contender),
        description: this.resolvedDesc(contender),
        extraClasses: ["so-contender-card"],
      });
      chost.appendChild(cc.el);
      this.cards.push(cc);
      cc.playReveal(0);
    }

    // The deck — the 5 held skills as the IDENTICAL full draft card, with the
    // factual rarity cues (FROZEN: facts on the cards, opinion in VERA's line).
    const deck = this.container.querySelector(".so-swap-deck");
    if (deck) {
      activeCards.forEach((card, i) => {
        if (!card) return;
        const hRank = rank(card.rarity);
        const isLowest = i === lowestSlot;
        // The factual rarity-vs-incoming read, framed as the SWAP RESULT for this
        // slot (FROZEN: facts on the cards, the opinion in VERA's line):
        //   LOWEST    — your weakest held slot (the natural swap target)
        //   ↑ UPGRADE — the incoming OUTRANKS this held card (swapping it in lifts the slot)
        //   ↓ LOWER   — the incoming is LOWER-rarity than this held card
        //   = SAME    — equal rarity
        const cue = isLowest
          ? "LOWEST"
          : cRank > hRank
            ? "↑ UPGRADE"
            : cRank < hRank
              ? "↓ LOWER"
              : "= SAME";
        const cueTone: "lowest" | "up" | "down" | undefined = isLowest
          ? "lowest"
          : cRank > hRank
            ? "up"
            : cRank < hRank
              ? "down"
              : undefined;
        const scOpts: ConstructorParameters<typeof SkillCard>[1] = {
          variant: "draft",
          name: this.resolvedName(card),
          description: this.resolvedDesc(card),
          extraClasses: ["so-deck-card"],
          cue,
        };
        if (cueTone) scOpts.cueTone = cueTone;
        const sc = new SkillCard(card, scOpts);
        sc.el.dataset["slot"] = String(i);
        // BL-118 — keyboard/AT parity: each deck card arms its slot via click OR
        // Enter/Space (role=button + tabindex), the targeting half of the choice.
        sc.el.setAttribute("aria-label", `Replace ${this.resolvedName(card)}`);
        makeKeyboardActivatable(sc.el, () => this.selectSlot(i, card));
        deck.appendChild(sc.el);
        this.cards.push(sc);
        sc.playReveal(80 + i * 55);
      });
    }

    // VERA — a balanced advisor panel CENTRED above KEEP; her face + her FULL
    // tip (no clip) agree with the situation. She is the voice of the decision.
    const veraHost = this.container.querySelector("#so-commander");
    if (veraHost) {
      veraHost.appendChild(
        createMascot({
          character: "vera",
          pose: adv.pose,
          frame: true,
          size: 96,
          quip: commsLine(adv.line),
          className: "so-commander-mascot",
        }),
      );
    }

    const keep = (): void => {
      announce("Kept current deck.");
      if (this.onSwapCancel) this.onSwapCancel();
      this.hide();
    };
    const keepBtn = this.container.querySelector(
      "#so-keep",
    ) as HTMLButtonElement | null;
    keepBtn?.addEventListener("click", keep);

    // CONFIRM SWAP commits the slot armed by a deck-card pick — one press, in
    // place (it never moves between arm and commit).
    this.container
      .querySelector("#so-confirm-go")
      ?.addEventListener("click", () => {
        if (this.selectedSlot != null) this.commitSwap(this.selectedSlot);
      });

    // Focus the safe, always-present anchor (KEEP DECK) so keyboard/AT users
    // land sensibly; they Tab to a deck card to arm CONFIRM SWAP.
    keepBtn?.focus();
  }

  /** BL-118 — a deck-card pick ARMS its slot: it lights CONFIRM SWAP in place,
   *  shows the OUT ▸ / IN ▸ summary (+ any PERMANENT path-warning), and previews
   *  the swap. Re-picking another card just re-targets; KEEP DECK is the cancel. */
  private selectSlot(slotIndex: number, oldCard: Card): void {
    const contender = this.pendingCard;
    if (!contender) return;
    this.selectedSlot = slotIndex;

    const oldName = this.resolvedName(oldCard);
    const newName = this.resolvedName(contender);
    const permanentNote =
      getPersistenceTag(oldCard).kind === "permanent"
        ? `<span class="so-confirm-warn">⚠ ${oldName} is PERMANENT — unloaded for the run</span>`
        : "";
    const action = this.container.querySelector(
      "#so-swap-action",
    ) as HTMLElement | null;
    if (action) {
      action.innerHTML = `
        <span class="so-confirm-out">OUT ▸ ${oldName}</span>
        <span class="so-confirm-arrow">→</span>
        <span class="so-confirm-in">IN ▸ ${newName}</span>
        ${permanentNote}`;
    }

    // preview highlight: flag the chosen slot OUT + the contender IN.
    this.container
      .querySelectorAll(".so-deck-card")
      .forEach((el) => el.classList.remove("so-pending-out"));
    this.container
      .querySelector(`.so-deck-card[data-slot="${slotIndex}"]`)
      ?.classList.add("so-pending-out");
    this.container
      .querySelector(".so-contender-card")
      ?.classList.add("so-pending-in");

    // arm CONFIRM SWAP in place.
    const go = this.container.querySelector(
      "#so-confirm-go",
    ) as HTMLButtonElement | null;
    if (go) {
      go.disabled = false;
      go.removeAttribute("aria-disabled");
      go.classList.add("armed");
    }
    this.container.classList.add("confirming");
    announce(`Replacing ${oldName} with ${newName}. Confirm swap or keep deck.`);
  }

  /** Commit: the eject/insert animation (paused budget), then the byte-untouched
   *  swap callback + close. Reduced-motion → instant. */
  private commitSwap(slotIndex: number): void {
    announce("Swap confirmed.");
    const cb = this.onSwap;
    const out = this.container.querySelector(
      `.so-deck-card[data-slot="${slotIndex}"]`,
    );
    out?.classList.add("so-ejecting");
    this.container.querySelector(".so-contender-card")?.classList.add("so-installing");

    const finalize = (): void => {
      if (cb) cb(slotIndex);
      this.hide();
    };
    if (isReducedMotion()) {
      finalize();
      return;
    }
    this.confirmTimer = setTimeout(finalize, 520);
  }

  private injectStyles(): void {
    const T = UI_TYPE;
    // BL-260 — the swap-mode gold is the central RARITY.legendary.mid (Glacier
    // gold), not a stale #FFC23D literal; paired glows derive from the same
    // token via color-mix so the fill and its halo stay one temperature.
    const gold = RARITY.legendary.mid;
    const goldGlow = (pct: number): string =>
      `color-mix(in srgb, ${gold} ${pct}%, transparent)`;
    const style = document.createElement("style");
    style.textContent = `
      #skill-overview { position:absolute; inset:0; display:flex; flex-direction:column; align-items:center; background:rgba(2,2,8,0.97); z-index:1500; opacity:0; pointer-events:none; transition:opacity ${UI_DURATION.settle}ms ${UI_EASE.settle}; overflow-y:auto; padding:24px 20px; font-family:${UI_FONT_STACK}; }
      #skill-overview.visible { opacity:1; pointer-events:all; }
      #skill-overview::before { content:''; position:absolute; inset:0; background:linear-gradient(90deg,transparent 49.5%,rgba(136, 224, 208,0.03) 49.5%,rgba(136, 224, 208,0.03) 50.5%,transparent 50.5%),linear-gradient(0deg,transparent 49.5%,rgba(136, 224, 208,0.03) 49.5%,rgba(136, 224, 208,0.03) 50.5%,transparent 50.5%); background-size:80px 80px; animation:circuitPulse 4s ease-in-out infinite; pointer-events:none; }
      @keyframes circuitPulse { 0%,100%{opacity:0.3} 50%{opacity:1} }
      #skill-overview::after { content:''; position:absolute; top:0; left:0; right:0; height:2px; background:linear-gradient(90deg,transparent,${COLORS.SENTINEL_GREEN},transparent); animation:scanLine 3s linear infinite; pointer-events:none; box-shadow:0 0 15px rgba(136, 224, 208,0.6); }
      @keyframes scanLine { 0%{top:0} 100%{top:100%} }

      .so-header { display:flex; align-items:center; justify-content:space-between; width:100%; max-width:1100px; margin-bottom:16px; position:relative; z-index:1; }
      .so-title { font-size:${roleFontSize("heading")}; color:${COLORS.SENTINEL_GREEN}; letter-spacing:3px; text-shadow:0 0 15px rgba(136, 224, 208,0.5); }
      .so-header--swap .so-title { color:${gold}; text-shadow:0 0 15px ${goldGlow(45)}; }
      .so-close-btn { font-family:${UI_FONT_STACK}; font-size:${roleFontSize("caption")}; color:#9FB89F; background:transparent; border:1px solid #2a5a3a; padding:6px 14px; cursor:pointer; letter-spacing:2px; transition:all 0.2s; }
      .so-close-btn:hover { background:rgba(136, 224, 208,0.12); border-color:${COLORS.SENTINEL_GREEN}; color:#fff; box-shadow:0 0 14px rgba(136, 224, 208,0.35); }

      /* ── normal mode: loadout summary + the Trainer-card binder ── */
      .so-loadout { width:100%; max-width:1100px; margin-bottom:18px; position:relative; z-index:1; border:1px solid #1a3a2a; padding:14px 16px; background:rgba(5,15,10,0.6); }
      .so-loadout-title { font-size:${roleFontSize("caption")}; color:#5a8a5a; letter-spacing:2px; margin-bottom:12px; text-transform:uppercase; }
      .so-loadout-slots { display:flex; flex-direction:column; gap:6px; }
      .so-slot { display:flex; align-items:center; gap:12px; padding:9px 12px; border:1px solid #0a2a1a; background:linear-gradient(90deg, color-mix(in srgb, var(--so-color,#1a5a3a) 14%, rgba(5,10,8,0.85)) 0%, rgba(5,10,8,0.85) 60%); transition:all 0.2s; position:relative; }
      .so-slot--filled { border-left:3px solid var(--so-color,#1a5a3a); box-shadow:inset 0 0 18px color-mix(in srgb, var(--so-color,#1a5a3a) 12%, transparent); }
      .so-slot--empty { border-style:dashed; border-color:#0a2a1a; opacity:0.6; }
      .so-slot-index { font-size:${roleFontSize("caption")}; color:#5a8a5a; min-width:28px; }
      .so-slot-rarity { font-size:${roleFontSize("caption")}; color:var(--so-color,#1a5a3a); min-width:16px; font-weight:700; text-shadow:0 0 6px color-mix(in srgb,var(--so-color) 60%, transparent); }
      .so-slot-name { font-size:${withUiScale(T.body.size + 1)}; color:#d6e6d6; flex:1; letter-spacing:0.5px; overflow-wrap: break-word; word-break: normal; }
      .so-slot--empty .so-slot-name { color:#2a3a2a; font-size:${roleFontSize("caption")}; }
      .so-slot-persistence { font-size:${roleFontSize("micro")}; padding:2px 7px; border:1px solid rgba(140,190,140,0.45); color:#9AC59A; background:rgba(12,26,12,0.6); }
      .so-slot-persistence--instant { color:${COLORS.AMBER_WARN}; border-color:rgba(232, 208, 152,0.55); background:rgba(50,38,6,0.65); }
      .so-slot-persistence--payload { color:${COLORS.AMBER_WARN}; border-color:rgba(232, 208, 152,0.8); background:rgba(60,44,0,0.85); }
      .so-slot-persistence--duration { color:#FFB884; border-color:rgba(255,164,96,0.5); background:rgba(42,24,10,0.65); }
      .so-slot-persistence--permanent { color:#88F2B8; border-color:rgba(136,242,184,0.5); background:rgba(8,32,20,0.65); }
      .so-slot-duration { font-size:${roleFontSize("micro")}; color:${COLORS.AMBER_WARN}; background:rgba(232, 208, 152,0.1); padding:2px 7px; border:1px solid rgba(232, 208, 152,0.3); }
      .so-section-title { width:100%; max-width:1100px; font-size:${roleFontSize("caption")}; color:#5a8a5a; letter-spacing:2px; margin-bottom:14px; padding-bottom:6px; border-bottom:1px solid #0a2a1a; position:relative; z-index:1; text-transform:uppercase; }
      /* BL-184 wrap guard on the Trainer-card cells (grid/deck/contender). */
      .so-grid, .so-swap-deck, .so-contender-host { overflow-wrap: break-word; word-break: normal; }
      .so-grid { display:grid; grid-template-columns:repeat(auto-fill, minmax(208px, 1fr)); gap:18px; width:100%; max-width:1100px; position:relative; z-index:1; padding-bottom:24px; }
      /* BL-77b: taller binder box + smaller art so the larger effect text fits in
         full (the .tc overflow:hidden frame was clipping PERMANENT at 262px).
         384/art-88 clears the worst-case 117-char description at ~208px cell width
         with comfortable margin (content ~350px). */
      .so-binder-card { width:100%; height:384px; --tc-skill-art-h:88px; }

      /* ── swap mode: the harmonious contender-vs-deck decision (ULTRA redesign) ──
         A balanced 3-column grid: the haloed contender (LEFT, centered, the
         challenger) · the VS→ · YOUR DECK as ONE even row of 5 on the RIGHT. The
         contender card + the deck row share the middle grid row so their centres
         align on one axis; the ▶ INCOMING tag + the deck title sit in the row above.
         VERA now lives CENTRED in the footer above KEEP (owner CR), not the grid. */
      #skill-overview.swap-mode::after { background:linear-gradient(90deg,transparent,${gold},transparent); box-shadow:0 0 15px ${goldGlow(50)}; }
      .so-swap {
        display:grid;
        grid-template-columns:minmax(206px,auto) auto minmax(0,1fr);
        grid-template-areas:
          "ctag   .    dtitle"
          "ccard  vs   drow";
        column-gap:22px; row-gap:10px; align-items:center;
        /* BL-77b: 1100 holds contender(206) + 5 deck(150) + VS + gaps with margin
           (206+22+~40+22+798 = 1088) while staying under the narrowest stage
           content width (~1130 at 4:3) — no horizontal overflow of the 5-in-a-row. */
        width:100%; max-width:1100px; position:relative; z-index:1; margin-bottom:16px;
      }
      .so-swap-tag { grid-area:ctag; justify-self:center; font-size:${roleFontSize("caption")}; letter-spacing:2px; color:${COLORS.SENTINEL_GREEN}; text-shadow:0 0 10px rgba(136, 224, 208,0.6); }
      .so-swap-deck-title { grid-area:dtitle; justify-self:start; align-self:end; font-size:${roleFontSize("caption")}; color:#5a8a5a; letter-spacing:2px; text-transform:uppercase; }
      /* the contender — a prominent, softly-haloed challenger; text-first type +
         a small hero emblem so even a long effect reads in full (no truncation). */
      .so-contender-host { grid-area:ccard; justify-self:center; display:flex; align-items:center; justify-content:center; }
      .so-contender-card { width:206px; height:324px; --tcs:0.9; --tc-skill-art-h:88px; --tc-skill-name-ws:normal; filter:drop-shadow(0 0 22px rgba(136, 224, 208,0.42)); }
      .so-swap-divider { grid-area:vs; justify-self:center; font-size:${roleFontSize("heading")}; color:${gold}; letter-spacing:1px; text-shadow:0 0 12px ${goldGlow(50)}; }
      /* YOUR DECK — one even, non-wrapping, vertically-centred row of 5; each a
         compact deck-scale card (small emblem, complete type) so every line is legible. */
      .so-swap-deck { grid-area:drow; display:flex; flex-wrap:nowrap; justify-content:center; align-items:center; gap:12px; padding-top:13px; }
      /* BL-77b: width held at 150 (5-in-a-row budget) — bigger via taller box +
         --tcs 0.6->0.72 + the raised effect-text floor, not extra width. 304/art-44
         clears the worst-case 117-char description at this narrow width. */
      .so-deck-card { width:150px; height:304px; flex:0 0 auto; cursor:pointer; transition:transform 0.15s ease; --tcs:0.72; --tc-skill-art-h:44px; --tc-skill-name-ws:normal; }
      .so-deck-card .tc-flavour, .so-contender-card .tc-flavour { display:none; } /* free the vertical room for legible effect text */
      .so-deck-card:hover { transform:translateY(-5px); }
      .so-deck-card.so-pending-out { outline:2px solid #FF6B6B; outline-offset:3px; }
      .so-contender-card.so-pending-in { outline:2px solid ${COLORS.SENTINEL_GREEN}; outline-offset:3px; }
      /* VERA — a centred advisor panel in the footer above KEEP, her FULL tip visible. */
      .so-swap-vera { width:100%; max-width:560px; margin:6px 0 4px; display:flex; flex-direction:column; align-items:center; gap:6px; box-sizing:border-box; border:1px solid #1a3a2a; border-left:3px solid #2a6a44; background:rgba(5,15,10,0.55); padding:10px 16px; border-radius:4px; }
      .so-swap-vera-label { align-self:flex-start; font-size:${roleFontSize("micro")}; letter-spacing:2px; color:#5a8a5a; text-transform:uppercase; }
      .so-swap-vera .operator-mascot { width:100%; }
      .so-swap-vera .operator-mascot.mascot-framed { width:100%; box-sizing:border-box; }
      .so-swap-vera .mascot-nameplate { display:none; }
      /* full tip — drop the 2-line clamp so VERA's whole advice shows (owner fix ③). */
      .so-swap-vera .operator-mascot.mascot-framed .mascot-quip {
        font-size:${roleFontSize("micro")}; line-height:1.32; text-align:center;
        display:block; -webkit-line-clamp:unset; height:auto; min-height:0;
        max-width:100%; overflow:visible;
      }

      .so-swap-foot { width:100%; max-width:1100px; display:flex; flex-direction:column; align-items:center; gap:8px; position:relative; z-index:1; padding-bottom:16px; }
      /* BL-118 — the action line doubles as the in-place OUT ▸ / IN ▸ summary
         once a slot is armed (a deck-card pick), so the read stays put. */
      .so-swap-action { font-size:${roleFontSize("body")}; color:#d6e6d6; letter-spacing:1px; display:flex; flex-wrap:wrap; justify-content:center; align-items:center; gap:6px 10px; min-height:1.4em; }
      .so-swap-stakes { font-size:${roleFontSize("caption")}; color:#FFB884; letter-spacing:1px; }
      /* BL-118 — the two and only choices, side by side: CONFIRM SWAP (armed by a
         deck pick) + KEEP DECK (always-safe cancel). One press each. */
      .so-swap-choices { margin-top:4px; display:flex; align-items:center; justify-content:center; gap:14px; flex-wrap:wrap; }
      .so-keep-btn { font-family:${UI_FONT_STACK}; font-size:${roleFontSize("body")}; color:${COLORS.SENTINEL_GREEN}; background:rgba(0,40,20,0.85); border:1px solid #1a5a3a; padding:10px 28px; cursor:pointer; letter-spacing:2px; transition:all 0.2s; }
      .so-keep-btn:hover { background:rgba(136, 224, 208,0.14); border-color:${COLORS.SENTINEL_GREEN}; color:#fff; box-shadow:0 0 16px rgba(136, 224, 208,0.4); }

      /* the eject/insert swap animation (paused budget). */
      .so-deck-card.so-ejecting { animation:soEject 0.5s cubic-bezier(0.16,1,0.3,1) forwards; }
      @keyframes soEject { 0%{opacity:1; transform:translateY(0) scale(1);} 100%{opacity:0; transform:translateY(40px) scale(0.7) rotate(6deg);} }
      .so-contender-card.so-installing { animation:soInstall 0.5s cubic-bezier(0.34,1.56,0.64,1); }
      @keyframes soInstall { 0%{transform:scale(1);} 40%{transform:scale(1.12); filter:drop-shadow(0 0 28px rgba(136, 224, 208,0.7));} 100%{transform:scale(1);} }

      /* BL-118 — the in-place OUT ▸ / IN ▸ summary cues (shown in .so-swap-action
         once a slot is armed) + the CONFIRM SWAP button (one of the two choices). */
      .so-confirm-out { color:#FF6B6B; letter-spacing:1px; }
      .so-confirm-arrow { color:${gold}; }
      .so-confirm-in { color:${COLORS.SENTINEL_GREEN}; letter-spacing:1px; }
      .so-confirm-warn { color:#FFB884; font-size:${roleFontSize("micro")}; letter-spacing:1px; width:100%; text-align:center; }
      .so-confirm-go { font-family:${UI_FONT_STACK}; font-size:${roleFontSize("body")}; color:#04140a; background:${COLORS.SENTINEL_GREEN}; border:1px solid ${COLORS.SENTINEL_GREEN}; padding:10px 28px; cursor:pointer; letter-spacing:2px; transition:all 0.2s; }
      .so-confirm-go:hover:not(:disabled) { box-shadow:0 0 16px rgba(136, 224, 208,0.6); }
      .so-confirm-go.armed { box-shadow:0 0 18px rgba(136, 224, 208,0.5); }
      /* dim + inert until a deck card is picked (no slot to commit yet). */
      .so-confirm-go:disabled { color:#3a5a44; background:transparent; border-color:#1a3a2a; cursor:not-allowed; letter-spacing:2px; }

      /* BL-193 — 44px touch floor on the three SkillOverview interactive controls
         (close, keep-deck, confirm-swap). Inside the fixed 1600×900 stage but still
         reached by touch on phone/tablet; cf. TowerSelector.ts:795-798. */
      @media (pointer:coarse) {
        .so-close-btn, .so-keep-btn, .so-confirm-go { min-height: 44px; }
      }
      /* (No viewport-size query: rendered inside the fixed 1600×900 stage and
         scaled to fit, so the swap layout keeps its two-column form everywhere.) */
      @media (prefers-reduced-motion: reduce) {
        #skill-overview::before, #skill-overview::after { animation:none; }
        .so-deck-card.so-ejecting, .so-contender-card.so-installing { animation:none; }
      }
    `;
    document.head.appendChild(style);
  }
}
